import { createIndexedDbPresetProvider } from "./indexeddb-preset-provider.js";
import {
  browserAccountStateSessionRepository,
} from "../account/account-state-session-repository.js";
import {
  createAccountAwareSavedPresetRepository,
} from "../account/account-aware-saved-preset-repository.js";
import {
  createSavedPresetAccountProvider,
} from "../account/saved-preset-account-provider.js";
import { presetCollectionContracts } from "./collection-contracts.js";
import {
  LEGACY_CALCULATOR_OVERLAY_V4_STORAGE_KEY,
  migrateLegacyCalculatorOverlayV4Storage,
} from "./calculator-overlay-v4-migration.js";
import {
  LEGACY_STORAGE_KEY,
  migrateLegacyPresetStorage,
} from "./legacy-preset-migration.js";
import { createLocalSavedPresetRepository } from "./saved-preset-repository.js";
import { createPresetWorkspaceRepository } from "./workspace-repository.js";
import { deepFreeze, frozenClone, isRecord } from "./preset-internals.js";

const BROWSER_SLOT = "__fishystuffPresetWorkspaceV1";
export const BROWSER_PRESET_WORKSPACE_BRAND = Symbol.for(
  "fishystuff.browser-preset-workspace.v1",
);
const BROADCAST_CHANNEL_NAME = "fishystuff-preset-workspace-v1";

const REQUIRED_METHODS = Object.freeze([
  "load",
  "state",
  "subscribe",
  "defineFixedPreset",
  "initialize",
  "edit",
  "usePreset",
  "useLocalPreset",
  "useFixed",
  "useRevision",
  "jumpToRevision",
  "undo",
  "redo",
  "saveAs",
  "updatePreset",
  "updateLocalPreset",
  "renamePreset",
  "clonePreset",
  "deletePreset",
  "resetCurrent",
  "clearCurrent",
  "resetAll",
  "importCollection",
  "exportCollection",
  "exportLocalPreset",
  "exportRawCollection",
  "collectionInfo",
  "fixedPresets",
  "bindCollectionRuntime",
]);

function isCompatible(value) {
  return Boolean(
    value
    && value[BROWSER_PRESET_WORKSPACE_BRAND] === 1
    && REQUIRED_METHODS.every((method) => typeof value[method] === "function"),
  );
}

function createBroadcaster(BroadcastChannelRef) {
  if (typeof BroadcastChannelRef !== "function") {
    return null;
  }
  try {
    return new BroadcastChannelRef(BROADCAST_CHANNEL_NAME);
  } catch (_error) {
    return null;
  }
}

function createSavedPresetRepository({
  options,
  provider,
  collectionContracts,
  windowRef,
}) {
  const hasSavedPresetOverride = options.savedPresets !== undefined;
  const hasSessionRepository = options.sessionRepository !== undefined;
  const hasAccountProvider = options.accountProvider !== undefined;
  if (hasSavedPresetOverride && (hasSessionRepository || hasAccountProvider)) {
    throw new TypeError(
      "Browser preset workspace saved authority is ambiguous.",
    );
  }
  if (hasSessionRepository !== hasAccountProvider) {
    throw new TypeError(
      "Browser preset workspace requires session and account providers together.",
    );
  }
  if (hasSavedPresetOverride) return options.savedPresets;

  const localRepository = createLocalSavedPresetRepository({
    provider,
    collectionContracts,
    clock: options.clock,
    createId: options.createId,
  });
  const hasInjectedAccountBoundary = hasSessionRepository && hasAccountProvider;
  const hasBrowserAccountBoundary = Boolean(
    windowRef
    && windowRef === globalThis.window
    && options.provider === undefined
    && typeof windowRef.__fishystuffApiBaseUrl === "string",
  );
  if (!hasInjectedAccountBoundary && !hasBrowserAccountBoundary) {
    return localRepository;
  }

  const sessionRepository = hasInjectedAccountBoundary
    ? options.sessionRepository
    : browserAccountStateSessionRepository();
  const accountProvider = hasInjectedAccountBoundary
    ? options.accountProvider
    : createSavedPresetAccountProvider({
      apiBaseUrl: options.apiBaseUrl ?? windowRef.__fishystuffApiBaseUrl,
      fetchImpl: options.fetchImpl ?? globalThis.fetch,
    });
  return createAccountAwareSavedPresetRepository({
    localRepository,
    sessionRepository,
    accountProvider,
  });
}

export function browserPresetWorkspace(options = {}) {
  if (!isRecord(options)) {
    throw new TypeError("Browser preset workspace options must be an object.");
  }
  const windowRef = options.window ?? globalThis.window ?? null;
  const occupied = windowRef?.[BROWSER_SLOT];
  if (occupied !== undefined) {
    if (!isCompatible(occupied)) {
      throw new TypeError("The shared browser preset workspace has an incompatible contract.");
    }
    return occupied;
  }
  const collectionContracts = options.collectionContracts || presetCollectionContracts();

  const provider = options.provider || createIndexedDbPresetProvider({
    indexedDB: options.indexedDB ?? globalThis.indexedDB,
    databaseName: options.databaseName,
  });
  const savedPresets = createSavedPresetRepository({
    options,
    provider,
    collectionContracts,
    windowRef,
  });
  const broadcaster = options.broadcaster === undefined
    ? createBroadcaster(options.BroadcastChannel ?? globalThis.BroadcastChannel)
    : options.broadcaster;
  const core = createPresetWorkspaceRepository({
    provider,
    savedPresets,
    collectionContracts,
    broadcaster,
    eventTarget: options.eventTarget ?? windowRef,
    clock: options.clock,
    createId: options.createId,
  });

  let storage = options.storage;
  if (storage === undefined) {
    try {
      storage = globalThis.localStorage;
    } catch (_error) {
      storage = null;
    }
  }
  let migrationPromise = null;
  let migrationState = frozenClone({ kind: "uninitialized" });

  async function ensureMigration() {
    if (!migrationPromise) {
      const pending = (async () => {
        const presetState = await migrateLegacyPresetStorage({
          provider,
          storage,
          collectionContracts,
          clock: options.clock,
          createId: options.createId,
        });
        if (presetState.kind !== "acknowledged") {
          return presetState;
        }
        return migrateLegacyCalculatorOverlayV4Storage({
          provider,
          storage,
          clock: options.clock,
          createId: options.createId,
        });
      })().then((state) => {
        migrationState = frozenClone(state);
        if (migrationState.kind !== "acknowledged") {
          migrationPromise = null;
        }
        return migrationState;
      }).catch((error) => {
        migrationPromise = null;
        throw error;
      });
      migrationPromise = pending;
    }
    return migrationPromise;
  }

  async function guarded(method, args) {
    const migration = await ensureMigration();
    if (migration.kind !== "acknowledged") {
      return migration;
    }
    return core[method](...args);
  }

  async function resetAllPresets(args) {
    const result = await core.resetAll(...args);
    if (result?.kind === "unavailable") {
      return result;
    }
    try {
      if (!storage || typeof storage.removeItem !== "function") {
        throw new TypeError("Legacy preset storage is unavailable.");
      }
      for (const key of [
        LEGACY_STORAGE_KEY,
        LEGACY_CALCULATOR_OVERLAY_V4_STORAGE_KEY,
      ]) {
        storage.removeItem(key);
      }
    } catch (_error) {
      return frozenClone({
        kind: "unavailable",
        operation: "remove-legacy-presets-after-reset",
        reason: "legacy-storage-remove-failed",
      });
    }
    migrationPromise = null;
    migrationState = frozenClone({ kind: "uninitialized" });
    return result;
  }

  function currentState(...args) {
    const state = core.state(...args);
    if (migrationState.kind === "uninitialized" || migrationState.kind === "acknowledged") {
      return state;
    }
    return frozenClone({
      ...migrationState,
      collectionKey: state.collectionKey,
    });
  }

  const facade = {
    [BROWSER_PRESET_WORKSPACE_BRAND]: 1,
    load(...args) {
      return guarded("load", args);
    },
    state: currentState,
    subscribe: core.subscribe,
    defineFixedPreset(...args) {
      return core.defineFixedPreset(...args);
    },
    initialize(...args) {
      return guarded("initialize", args);
    },
    edit(...args) {
      return guarded("edit", args);
    },
    usePreset(...args) {
      return guarded("usePreset", args);
    },
    useLocalPreset(...args) {
      return guarded("useLocalPreset", args);
    },
    useFixed(...args) {
      return guarded("useFixed", args);
    },
    useRevision(...args) {
      return guarded("useRevision", args);
    },
    jumpToRevision(...args) {
      return guarded("jumpToRevision", args);
    },
    undo(...args) {
      return guarded("undo", args);
    },
    redo(...args) {
      return guarded("redo", args);
    },
    saveAs(...args) {
      return guarded("saveAs", args);
    },
    updatePreset(...args) {
      return guarded("updatePreset", args);
    },
    updateLocalPreset(...args) {
      return guarded("updateLocalPreset", args);
    },
    renamePreset(...args) {
      return guarded("renamePreset", args);
    },
    clonePreset(...args) {
      return guarded("clonePreset", args);
    },
    deletePreset(...args) {
      return guarded("deletePreset", args);
    },
    resetCurrent(...args) {
      return guarded("resetCurrent", args);
    },
    clearCurrent(...args) {
      return guarded("clearCurrent", args);
    },
    resetAll(...args) {
      return resetAllPresets(args);
    },
    importCollection(...args) {
      return guarded("importCollection", args);
    },
    exportCollection(...args) {
      return guarded("exportCollection", args);
    },
    exportLocalPreset(...args) {
      return guarded("exportLocalPreset", args);
    },
    exportRawCollection(...args) {
      return core.exportRawCollection(...args);
    },
    collectionInfo: core.collectionInfo,
    fixedPresets: core.fixedPresets,
    bindCollectionRuntime: core.bindCollectionRuntime,
    migrationState() {
      return frozenClone(migrationState);
    },
  };
  deepFreeze(facade);
  if (windowRef && typeof windowRef === "object") {
    windowRef[BROWSER_SLOT] = facade;
  }
  return facade;
}
